import { type HTMLAttributes, useId } from "react";

export type LogoVariant = "full" | "icon" | "mark";

/** Wordmark fiel a la marca: PainPulse oscuro · AI en gradiente, ligeramente más chico */
function PainPulseWordMark({ compact }: { compact?: boolean }) {
  const base =
    compact
      ? "text-base sm:text-lg"
      : "text-lg sm:text-2xl";
  const suffix = compact ? "text-[0.8em]" : "text-[0.78em] sm:text-[0.82em]";
  return (
    <span className={`inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-1 ${base} whitespace-nowrap select-none`}>
      <span className="font-bold tracking-tight text-pp-main">PainPulse</span>
      <span
        className={`font-bold tracking-tight bg-pp-gradient bg-clip-text text-transparent ${suffix}`}
      >
        AI
      </span>
    </span>
  );
}

function SymbolGlyph({ size, gradId }: { size: number; gradId: string }) {
  const fillRef = `url(#${gradId})`;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 512 512"
      aria-hidden
      focusable={false}
      className="shrink-0 drop-shadow-[0_6px_24px_rgba(0,0,0,0.35)]"
    >
      <defs>
        <linearGradient id={gradId} x1="256" y1="88" x2="256" y2="432" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#B026FF" />
          <stop offset="100%" stopColor="#2D7CFF" />
        </linearGradient>
      </defs>
      <rect width="512" height="512" fill="#0B1020" />
      <rect width="468" height="468" x="22" y="22" rx="102" ry="102" fill="#111827" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
      <path
        fill={fillRef}
        d="M 114 426
           L 114 94
           L 266 94
           C 382 94 418 152 418 226
           C 418 304 354 354 244 354
           L 202 354
           L 202 426
           Z"
      />
      <path
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="40"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M 56 282 H 128 L 170 282 L 200 246 L 232 330 L 260 282 H 456"
      />
    </svg>
  );
}

type LogoProps = {
  variant?: LogoVariant;
  /** Lockup oficial (fondo blanco arriba en el archivo maestro); útil sobre hero claro. */
  raster?: boolean;
  className?: string;
  iconSize?: number;
} & Omit<HTMLAttributes<HTMLDivElement>, "children">;

/**
 * PainPulse AI — símbolo (P + pulso en squircle navy), raster opcional, o solo wordmark.
 * El PNG maestro está en `/brand/logo-painpulse-ai.png`.
 */
export function Logo({
  variant = "full",
  raster = false,
  className,
  iconSize = 40,
  ...rest
}: LogoProps) {
  const slug = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const gradId = `pp-grad-${slug || "sym"}`;

  if (variant === "mark") {
    return (
      <div className={`inline-flex flex-col leading-none ${className ?? ""}`} {...rest}>
        <PainPulseWordMark />
      </div>
    );
  }

  if (variant === "icon") {
    return (
      <div className={`inline-flex shrink-0 ${className ?? ""}`} {...rest}>
        <SymbolGlyph size={iconSize} gradId={gradId} />
      </div>
    );
  }

  if (raster) {
    return (
      <div className={`inline-flex items-end ${className ?? ""}`} {...rest}>
        <img
          src="/brand/logo-painpulse-ai.png"
          alt="PainPulse AI"
          className="h-10 w-auto max-w-[min(100%,300px)] sm:h-11"
          decoding="async"
        />
      </div>
    );
  }

  return (
    <div className={`inline-flex items-end gap-2.5 sm:gap-3 ${className ?? ""}`} {...rest}>
      <SymbolGlyph size={iconSize} gradId={gradId} />
      <PainPulseWordMark compact={iconSize < 40} />
    </div>
  );
}
